import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { personalData } from '../data/portfolioData';
import AnimatedSection from './AnimatedSection';
import { FaCode, FaServer, FaBrain, FaUsers, FaLaptopCode } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const Skills = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('frontend');
  
  const categories = [
    { id: 'frontend', name: t('skills.categories.frontend'), icon: FaCode, color: '#61DAFB' },
    { id: 'backend', name: t('skills.categories.backend'), icon: FaServer, color: '#339933' },
    { id: 'ai_ml', name: t('skills.categories.ai'), icon: FaBrain, color: '#FF6B35' },
    { id: 'soft', name: t('skills.categories.soft'), icon: FaUsers, color: '#10B981' }
  ];
  
  const currentSkills = personalData.skills[activeCategory] || [];

  return (
    <section className="py-32 relative overflow-hidden">
      {/* Animirana pozadina */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-[100px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary rounded-full blur-[100px]"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-4"
            >
              <FaLaptopCode className="text-5xl text-gradient" />
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {t("skills.title")}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t("skills.subtitle")}
            </p>
          </div>
        </AnimatedSection>

        {/* Kategorije - Moderni tabovi */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <motion.button
                key={cat.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(cat.id)}
                className={`group relative px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 ${
                  isActive
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                style={{
                  background: isActive ? `linear-gradient(135deg, ${cat.color}CC, ${cat.color}40)` : 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: isActive ? `1px solid ${cat.color}` : '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <Icon size={18} style={{ color: isActive ? cat.color : 'currentColor' }} />
                <span>{cat.name}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-full -z-10"
                    style={{ background: `linear-gradient(135deg, ${cat.color}40, transparent)` }}
                    transition={{ type: "spring", duration: 0.5 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Skills grid - Moderni prikaz */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
          >
            {currentSkills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <div className="relative glass-effect rounded-2xl p-6 overflow-hidden">
                  {/* Background glow */}
                  <div 
                    className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                    style={{ background: skill.color }}
                  />
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                      style={{ 
                        background: `${skill.color}20`,
                        boxShadow: `0 0 20px ${skill.color}40`
                      }}
                    >
                      {skill.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-400 transition-all duration-300">
                        {t(`skills.softNames.${skill.name}`, { defaultValue: skill.name })}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-400">{t('skills.proficiency')}</span>
                        <span className="text-sm font-semibold" style={{ color: skill.color }}>
                          {skill.level}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Modern progress bar */}
                  <div className="relative h-2 bg-gray-700/50 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${skill.level}%` }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                      className="absolute h-full rounded-full"
                      style={{ 
                        background: `linear-gradient(90deg, ${skill.color}, ${skill.color}80)`,
                        boxShadow: `0 0 10px ${skill.color}`
                      }}
                    />
                  </div>
                  
                  {/* Skill level dots */}
                  <div className="flex justify-between mt-3">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                          i < Math.ceil(skill.level / 20)
                            ? 'opacity-100'
                            : 'opacity-30'
                        }`}
                        style={{ 
                          backgroundColor: i < Math.ceil(skill.level / 20) ? skill.color : '#4B5563',
                          boxShadow: i < Math.ceil(skill.level / 20) ? `0 0 5px ${skill.color}` : 'none'
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Tech stack summary badges */}
        <AnimatedSection>
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-4">{t('skills.exploring')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {["WordPress", "Rust", "GraphQL", "MongoDB", "Raspberry Pi", "ArduinoUno"].map((tech, i) => (
                <motion.span
                  key={tech}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="px-4 py-2 glass-effect rounded-full text-sm text-gray-300 hover:text-white transition-all cursor-default"
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Skills;